import { FC, useState, useEffect } from 'react';
import Modal from '@components/Modal';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ProgressIndicator from '@components/ProgressIndicator';
import * as format from '@format';
import { FollowupHistoryStatus } from '@typings/api-models';
import Spacer from '@components/Spacer';
import { Collaborator } from '@components/AssignCollaboratorsToAttachmentForm';
import UserImage from '@components/UserImage';
import s from './FollowupHistoryModal.module.scss';

type Props = {
  onClose: () => void;
  attachmentId: string;
  assignees: Collaborator[];
};

const FollowupHistoryModal: FC<Props> = ({ onClose, attachmentId, assignees }) => {
  const [nextFollowups, setNextFollowups] = useState<Awaited<ReturnType<typeof tsClient.getAttachmentFollowupHistory>>['items'][number][]>([]);
  const [pastFollowups, setPastFollowups] = useState<Awaited<ReturnType<typeof tsClient.getAttachmentFollowupHistory>>['items'][number][]>([]);
  const {
    data,
    getData,
    fetchingState: { isFetching },
  } = useNoodleApi(tsClient.getAttachmentFollowupHistory);

  useEffect(() => {
    getData({ id: attachmentId, page: 1, perPage: 1000 });
  }, [attachmentId, getData]);

  useEffect(() => {
    if (data?.items) {
      setNextFollowups(data.items.filter(i => i.status === FollowupHistoryStatus.SCHEDULED));
      setPastFollowups(data.items.filter(i => i.status === FollowupHistoryStatus.SENT));
    }
  }, [data]);

  return (
    <Modal hideBottomInset title={`Follow-ups`} onClose={onClose} >
      <div className={s.container}>
        {isFetching && <ProgressIndicator isCentered />}
        {nextFollowups.length > 0 && (
          <>
            <p className="caption-bold">
              Next follow-up to{' '}
              {assignees.length > 0
                && assignees.map((assignee, idx) => (
                  <span className={s.assignee} key={assignee.id}>
                    <UserImage size={16} name={assignee.name} color={assignee.color} url={assignee.logo} /> {assignee.name}
                    {idx < assignees.length - 1 && ', '}
                  </span>
                ))}
            </p>
            {nextFollowups.map(followup => (
              <div className={s.followup} key={followup.id}>
                <p className="body-sm">{followup.followupMessageText}</p>
                <Spacer />
                <p className="caption">{format.datetimeLocalTZ.withTimezone({ datetime: followup.scheduledAt })}</p>
              </div>
            ))}
          </>
        )}
        {pastFollowups.length > 0 && (
          <>
            {nextFollowups.length > 0 && <Spacer size="20px" />}
            <p className="caption-bold">Follow-up history</p>
            {pastFollowups.map(followup => (
              <div className={s.followup} key={followup.id}>
                <p className="body-sm">{followup.followupMessageText}</p>
                <Spacer />
                <p className="caption">{format.datetimeLocalTZ.withTimezone({ datetime: followup.scheduledAt })}</p>
              </div>
            ))}
          </>
        )}
      </div>
    </Modal>
  );
};

export default FollowupHistoryModal;
